import { authApiV2, impersonationApi } from '@/api/endpoints/auth';
import { markExplicitLogout } from '@/features/auth/lib/explicit-logout';
import { isSsiStaffRole } from '@/features/orders/lib/order-ssi-roles';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth.store';

export type SignOutOptions = {
  /** When true, POST /auth/impersonation/exit before revoking the session. */
  exitImpersonation?: boolean;
};

/**
 * End the local + Supabase + server session. Awaits Supabase `signOut` before
 * clearing the Zustand store so useAuthInit cannot re-hydrate the impersonated
 * user from a stale `getSession()` after `realUser` is dropped.
 */
export async function performSignOut(
  options: SignOutOptions = {},
): Promise<void> {
  const { refreshToken, realUser, logout, user } = useAuthStore.getState();
  const shouldClearRememberMe = Boolean(options.exitImpersonation && realUser);
  const roleKnown = Boolean(user?.role);
  const operatorIsSsi = isSsiStaffRole(user?.role) || isSsiStaffRole(realUser?.role);

  if (options.exitImpersonation && realUser) {
    try {
      await impersonationApi.exit(refreshToken, realUser.userId);
    } catch {
      // Best-effort — local + Supabase sign-out still run.
    }
  }

  if (isSupabaseLoginEnabled()) {
    try {
      await getSupabaseClient().auth.signOut({ scope: 'global' });
    } catch {
      try {
        await getSupabaseClient().auth.signOut({ scope: 'local' });
      } catch {
        // Best-effort — Zustand clear still runs.
      }
    }
  }

  const tokenForLogout = useAuthStore.getState().refreshToken ?? refreshToken;
  if (tokenForLogout) {
    try {
      await authApiV2.logout(tokenForLogout, {
        clearRememberMe: shouldClearRememberMe,
      });
    } catch {
      // Best-effort — cookie/session clear still runs.
    }
  }

  // Company users only, once /auth/me has a role. Unknown role and SSI
  // fail open so SSI Login still starts Microsoft on the first click.
  if (roleKnown && !operatorIsSsi) {
    markExplicitLogout();
  }
  logout();
}
